import { useId } from "react";
import {
  ghostBtnClass,
  mfgInlineInputClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { sanitizeSlotLabelInput } from "@/lib/manufacturing/slotLabel";
import type { VariantSlotScaleRule } from "@/lib/manufacturing/variantConfig";

function emptyRule(): VariantSlotScaleRule {
  return { slot_label: "", scale_factor: 1.33 };
}

/** Catalog slot scale rules — estimates that materialize to absolute deltas on apply. */
export function ManufacturingNamedVariantSlotScaleEditor({
  rules,
  orgSlotLabels,
  onChange,
}: {
  rules: readonly VariantSlotScaleRule[];
  orgSlotLabels: readonly string[];
  onChange: (next: VariantSlotScaleRule[]) => void;
}) {
  const listId = useId();
  const rows = [...rules];

  function patch(index: number, patch: Partial<VariantSlotScaleRule>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-gray-500 dark:text-zinc-400">
        Slot scale estimates: new total ≈ parent qty × factor; we add the difference using that
        recipe&apos;s slot material (e.g. factor 1.33 on joists for 16&quot;→12&quot; OC). Review
        quantities after apply.
      </p>
      {rows.length === 0 ? (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">No slot scale rules yet.</p>
      ) : null}
      {rows.map((row, index) => (
        <div
          key={`${listId}-${index}`}
          className="flex flex-wrap items-end gap-2 rounded-md border border-gray-100 p-2 dark:border-zinc-700"
        >
          <label className="flex min-w-[140px] flex-1 flex-col gap-1">
            <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
              Slot label
            </span>
            <input
              className={mfgInlineInputClass}
              list={`${listId}-slot-${index}`}
              placeholder="e.g. joist"
              value={row.slot_label}
              onChange={(e) =>
                patch(index, { slot_label: sanitizeSlotLabelInput(e.target.value) })
              }
            />
            <datalist id={`${listId}-slot-${index}`}>
              {orgSlotLabels.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>
          <label className="flex min-w-[100px] flex-col gap-1">
            <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
              Scale factor
            </span>
            <input
              className={mfgInlineInputClass}
              type="number"
              min={0.0001}
              step="any"
              value={row.scale_factor}
              onChange={(e) => {
                const n = Number(e.target.value);
                patch(index, {
                  scale_factor: Number.isFinite(n) && n > 0 ? n : 1,
                });
              }}
            />
          </label>
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className={ghostBtnClass}
        onClick={() => onChange([...rows, emptyRule()])}
      >
        + Add slot scale rule
      </button>
    </div>
  );
}
